import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  ActionResultDto,
  MarketDataCacheKey,
  MarketDataTableInput,
  TenantActionInput,
  TenantCacheRowDto,
  TenantIntegrationRowDto,
  TenantLookupDto,
} from '@/api/types/marketDataIntegration';

// ABP conventional-controller mapping of
// Finkit.Host.MarketDataIntegration.MarketDataIntegrationAdminAppService
// (no HTTP attributes -> pure convention; Get* => HTTP GET with the Get prefix
// stripped from the route, the complex DTO binding from the query string):
//   GetCacheTableAsync(input)       -> GET  .../cache-table
//   GetIntegrationTableAsync(input) -> GET  .../integration-table
//   GetTenantsAsync()               -> GET  .../tenants
//   RunIntegrationAsync(input)      -> POST .../run-integration
//   StopIntegrationAsync(input)     -> POST .../stop-integration
const BASE = '/api/app/market-data-integration-admin';

export const marketDataIntegrationApi = {
  async getTenants(): Promise<TenantLookupDto[]> {
    const { data } = await http.get<TenantLookupDto[]>(`${BASE}/tenants`);
    return data;
  },

  async getCacheTable(input: MarketDataTableInput): Promise<PagedResult<TenantCacheRowDto>> {
    const { data } = await http.get<PagedResult<TenantCacheRowDto>>(`${BASE}/cache-table`, {
      params: input,
    });
    return data;
  },

  async getIntegrationTable(
    input: MarketDataTableInput,
  ): Promise<PagedResult<TenantIntegrationRowDto>> {
    const { data } = await http.get<PagedResult<TenantIntegrationRowDto>>(
      `${BASE}/integration-table`,
      { params: input },
    );
    return data;
  },

  async runIntegration(input: TenantActionInput): Promise<ActionResultDto> {
    const { data } = await http.post<ActionResultDto>(`${BASE}/run-integration`, input);
    return data;
  },

  async stopIntegration(input: TenantActionInput): Promise<ActionResultDto> {
    const { data } = await http.post<ActionResultDto>(`${BASE}/stop-integration`, input);
    return data;
  },

  // Legacy export is a Razor page handler returning an Excel file. Kept here so
  // the React page can build the same download URL once a bearer-auth endpoint
  // is available (see the page for the caveat).
  exportUrl(tenantId: string, cacheKey: MarketDataCacheKey): string {
    return `/Host/MarketDataIntegration?handler=Export&tenantId=${encodeURIComponent(
      tenantId,
    )}&cacheKey=${cacheKey}`;
  },
};
